import { useMutation } from '@tanstack/react-query';

import { pollLookalikes } from '../api/persona-lookalikes';
import { logger } from '../lib/logger';

const log = logger.child({ module: 'persona-lookalikes' });

function pollErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'The poll failed.';
}

/**
 * One yes/no question, fanned out to the population — not TanStack-cached
 * server state like the rest of the modal: a poll result isn't persisted, so
 * there's nothing for a query key to point at. Plain mutation state (the
 * last question asked and its result) is all this needs, and it resets
 * itself whenever the persona changes, same as everything else this
 * component is keyed on.
 */
export function useLookalikePoll(personaId: string) {
  const mutation = useMutation({
    mutationFn: (question: string) => pollLookalikes(personaId, question),
    onError: (err) => {
      log.error({ err }, 'lookalike poll failed');
    },
  });

  return {
    error: mutation.error ? pollErrorMessage(mutation.error) : null,
    polling: mutation.isPending,
    reset: mutation.reset,
    result: mutation.data ?? null,
    run: (question: string) => {
      mutation.mutate(question);
    },
  };
}
